import { inject, Injectable } from "@angular/core";
import { PCP_LOGGER } from "@pcp/logging";

@Injectable({
    providedIn: 'root',
})
export class ScreenWakeLockService {
    private readonly log = inject(PCP_LOGGER).createScoped('classroom');
    private wakeLock: null|WakeLockSentinel = null;

    /**
     * NOTE: iOS denies wake lock requests until the user interacts with the page.
     * This Promise resolved after the first click or touch event on the page.
     * Use this in order to retry acquiring the lock after the first user interaction.
     */
    private awaitFirstScreenPress = () => new Promise<void>((resolve) => {
            const onClick = () => {
                resolve();
                document.removeEventListener('click', onClick);
            };
            document.addEventListener('click', onClick, { passive: true });
        })


    async requestLock(isRetry?: boolean): Promise<void> {
        if (!('wakeLock' in navigator)) {
            this.log.warn('Wake Lock API not supported in this browser');
            return Promise.resolve();
        }

        try {
            this.wakeLock = await navigator.wakeLock.request('screen');
            this.wakeLock.onrelease = () => {
                this.wakeLock = null;
            };

        } catch (error) {
            if (error instanceof Error && error.name === 'NotAllowedError' && !isRetry) {
                // Retry after user interaction
                await this.awaitFirstScreenPress();
                await this.requestLock(true);
            }
        }
    }


    async releaseLock() {
        if (!this.wakeLock) {
            return Promise.resolve();
        }

        try {
            await this.wakeLock.release();
            this.wakeLock = null;
        } catch (error) {
            this.log.error('Failed to release wake lock', error instanceof Error ? error : undefined);
        }
    }

    isWakeLockActive() {
        return this.wakeLock !== null && !this.wakeLock.released;
    }

    getWakeLock() {
        return this.wakeLock;
    }
}
